/**
 * External load-detail URL resolution.
 * For supported sources, returns the deep link to the external loadboard's detail page.
 * Returns null if the source isn't supported.
 */

import { CanonicalLoad } from '@numeo/types-core'

// LIS strips the `SOURCE|` prefix from externalId by default (load-hub omits
// `stripSourcePrefix` → defaults to true), but Load Radar opts out and keeps
// the composite. Accept both shapes so a button shows up in either flow.
function stripPrefix(externalId: string): string {
    const pipe = externalId.indexOf('|')
    return pipe >= 0 ? externalId.slice(pipe + 1) : externalId
}

export function getExternalLoadUrl(load: CanonicalLoad): string | null {
    if (load.sourceType === 'BEON' && load.externalId) {
        const guid = stripPrefix(load.externalId)
        return guid ? `https://beoncarrier.gobeon.com/LoadDetails?loadId=${guid}` : null
    }
    if (load.sourceType === 'ARRIVE' && load.loadID) {
        return `https://carrier.arrivelogistics.com/load/${load.loadID}`
    }
    if (load.sourceType === 'SPOT' && load.loadID) {
        return `https://myspotcarrier.com/available-freight/${load.loadID}`
    }
    if (load.sourceType === 'JB_HUNT' && load.externalId) {
        const jobId = stripPrefix(load.externalId)
        return jobId ? `https://scm.jbhunt.com/carrier/ngx/load-board/load-details/${jobId}` : null
    }
    if ((load.sourceType === 'TRUCKSTOP' || load.sourceType === 'TRUCKSTOP_ACCOUNT') && load.loadID) {
        return `https://app.truckstop.com/PostingDetails/Loads/${load.loadID}`
    }
    // Convoy — `loadID` is now the friendly `convoyId` ("29722999") for display. The deep
    // link still needs the full S-prefixed offer-id (the unique routing key Convoy uses on
    // /offer/{id}); we stash that in `externalId` as `CONVOY|S-token` and pull it back here.
    if (load.sourceType === 'CONVOY' && load.externalId) {
        const offerToken = stripPrefix(load.externalId)
        return offerToken ? `https://carrier.convoy.com/offer/${encodeURIComponent(offerToken)}` : null
    }
    // Uber Freight — externalId is `UBER_FREIGHT|{offerUUID}`. Deep link to offer details page.
    if (load.sourceType === 'UBER_FREIGHT' && load.externalId) {
        const offerUUID = stripPrefix(load.externalId)
        return offerUUID ? `https://www.uber.com/freight/carriers/fleet/offer-details/${offerUUID}` : null
    }
    // Emerge — `loadID` is the friendly reference (e.g. "Q114016929"). The deep link wants the
    // numeric tail after the "Q11" shipper prefix, e.g. "4016929".
    if (load.sourceType === 'EMERGE' && load.loadID) {
        const ref = String(load.loadID)
        const id = ref.startsWith('Q11') ? ref.slice(3) : ref
        return id ? `https://app.emergemarket.io/cp/source/spot/open/${id}` : null
    }
    return null
}
